import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from 'axios';
import { errorCatch } from './errorCatcher.reducer';
import { InitState as CountryConfigState } from './countryConfig.reducer';
import countries from '../../public/static/countries.json';

export type Country = {
  name: string;
  id: string;
  iso2: string;
  iso3: string;
  order: number;
};

export type CountriesDataType = {
  '@odata.count': number;
  value: Country[];
};

export type RegionsDataType = {
  '@odata.count': number;
  value: Region[];
};

export type Region = {
  countryId: string;
  name: string;
  id: string;
  order: number;
  localCode?: string;
};

type Territory = {
  id: string;
  name: string;
  localCode: string;
};

export type InitState = {
  countries: {
    status: string;
    data: Country[];
  };
  regions: {
    status: string;
    data: Region[];
  };
  billingRegions: {
    status: string;
    data: Region[];
  };
};

export const initialState = {
  countries: {
    status: '',
    data: []
  },
  regions: {
    status: '',
    data: []
  },
  billingRegions: {
    status: '',
    data: []
  }
} as InitState;

const isEnhancedBillingCountry = (countryIso2: string, state: { countryConfig: CountryConfigState }): boolean => {
  if (!countryIso2) return false;
  const enabledCountries = state.countryConfig?.enhancedBillingEnabledCountries ?? [];
  return enabledCountries.some(code => code.toUpperCase() === countryIso2.toUpperCase());
};

type TerritoriesODataResponse = {
  '@odata.count'?: number;
  value: Territory[];
};

const getTerritoriesAsRegions = async (countryIso2: string): Promise<Region[]> => {
  const { data } = await Axios.get<TerritoriesODataResponse | Territory[]>(
    `/api/v3.03/territories?$filter=territoryType eq null and countryIso2Code eq '${countryIso2}'`
  );

  const territories: Territory[] = Array.isArray(data) ? data : data.value;

  return territories
    .map((territory: Territory, index: number) => ({
      id: territory.id,
      name: territory.name,
      countryId: '',
      order: index,
      localCode: territory.localCode
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const getLegacyRegions = async (countryIso2: string): Promise<Region[]> => {
  let regions: Region[] = [];
  let oDataCount = 0;

  do {
    const { data } = await getRegionsValues(regions.length, countryIso2);
    regions = regions.concat(data.value);
    oDataCount = data['@odata.count'];
  } while (regions.length < oDataCount);

  return regions;
};

export const getRegions = createAsyncThunk(
  'userLocationData/getRegions',
  async ({ countryIso2, isBilling = false }: { countryIso2: string; isBilling?: boolean }, { dispatch, getState }) => {
    try {
      const state = getState() as { countryConfig: CountryConfigState };

      if (isEnhancedBillingCountry(countryIso2, state)) {
        return await getTerritoriesAsRegions(countryIso2);
      }

      return await getLegacyRegions(countryIso2);
    } catch (error) {
      const label = isBilling ? 'getBillingRegions' : 'getRegions';
      dispatch(errorCatch({ place: `userLocationData/${label}.rejected`, data: error }));
      return Promise.reject(error);
    }
  }
);

const getRegionsValues = async ($skip: number, countryIso2: string) =>
  Axios.get<RegionsDataType>('/api/v2/Location/states', {
    params: {
      $filter: `countryIso2 eq '${countryIso2}'`,
      $skip,
      $orderby: 'name'
    }
  });

export const getCountries = createAsyncThunk<CountriesDataType>('userLocationData/getCountries', (_, { dispatch }) => {
  try {
    return JSON.parse(JSON.stringify(countries));
  } catch (error) {
    dispatch(errorCatch({ place: 'userLocationData/getCountries.rejected', data: error }));
    return Promise.reject(error);
  }
});

const slice = createSlice({
  name: 'userLocationData',
  initialState,
  reducers: {},
  extraReducers: {
    // getRegions
    [getRegions.pending.toString()]: (state, action) => {
      const target = action.meta.arg.isBilling ? state.billingRegions : state.regions;
      target.status = 'loading';
    },
    [getRegions.fulfilled.toString()]: (state, action) => {
      const target = action.meta.arg.isBilling ? state.billingRegions : state.regions;
      target.data = action.payload;
      target.status = 'success';
    },
    [getRegions.rejected.toString()]: (state, action) => {
      const target = action.meta.arg.isBilling ? state.billingRegions : state.regions;
      target.status = 'rejected';
    },
    // getCountries
    [getCountries.pending.toString()]: state => {
      state.countries.status = 'loading';
    },
    [getCountries.fulfilled.toString()]: (state, action) => {
      state.countries.data = action.payload;
      state.countries.status = 'success';
    },
    [getCountries.rejected.toString()]: state => {
      state.countries.status = 'rejected';
    }
  }
});

export default slice.reducer;
